import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { LayoutService } from '@app/core/services/layout.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { BehaviorSubject, catchError, debounceTime, EMPTY, from, Subject, switchMap, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserGroupService {
    private userGroupSubject = new BehaviorSubject<UserGroupGet | null>(null);

    public currentUserGroup$ = this.userGroupSubject.asObservable();

    private getUserGroupSubject = new Subject<GetUserGroupParams | null>();

    constructor(private datatask: DataTaskService, private layout: LayoutService) {
        this.getUserGroupSubject
            .pipe(
                debounceTime(150),
                switchMap((value) => {
                    if (!!value) {
                        return from(this.getUserGroup(value)).pipe(catchError(() => of(null)));
                    } else {
                        return of(null);
                    }
                })
            )
            .subscribe({ next: (value) => this.userGroupSubject.next(value) });
    }

    public setCurrentUserGroup(params: GetUserGroupParams | null) {
        this.getUserGroupSubject.next(params);
    }

    /** @datatask 3483 */
    public async getSetting(params: { p2_setting_keyno: string | number }): Promise<UserGroupSettingGet> { return (await this.datatask.Post(3483, params))[0]; }
    /** @datatask 37 */
    public getSettings(params: { p2_setting_keyno: string | number }): Promise<Item[]> { return this.datatask.Post(37, params); }
    /** @datatask 1114 */
    public async changeAllSettings(params: { p2_setting_keyno: string | number; onoff: '0' | '1' }): Promise<ErrorMessageResponse> { return (await this.datatask.Post(1114, params))[0]; }
    /** @datatask 38 */
    public async getSettingEdit(params: { p2_usergroup_setting_keyno: string | number }): Promise<UserGroupSettingEditGet> { return (await this.datatask.Post(38, params))[0]; }
    /** @datatask 39 */
    public async saveSetting(page: UserGroupSettingEditGet): Promise<ErrorMessageResponse> {
        const response = (await this.datatask.Post(39, page))[0];
        // Legacy userService.init() is a no-op; retain its subsequent layout notification.
        this.layout.onUsergroupSettingChanged();
        return response;
    }
    /** @datatask 40 */
    public getTables(params: UserGroupTableParams): Promise<Item[]> { return this.datatask.Post(40, params); }
    /** @datatask 87 */
    public async getTableEdit(params: { p2_usergroup_table_keyno: string | number }): Promise<UserGroupTableEditGet> { return (await this.datatask.Post(87, params))[0]; }
    /** @datatask 89 */
    public saveTable(page: UserGroupTableEditGet): Promise<ErrorMessageResponse[]> { return this.datatask.Post(89, page); }

    /**
     * Retrieves user-group for the given params.
     *
     * @datatask 99
     * @datataskName sp_ws_p2_usergroup_get
     * @param params usergroup keyno.
     * @returns promise containing the user group.
     */
    public async getUserGroup(params: GetUserGroupParams): Promise<UserGroupGet> {
        return (await this.datatask.Post(99, params))[0];
    }

    /**
     * Saves the given usergroup, either update or add.
     *
     * @datatask 100
     * @datataskName sp_ws_p2_usergroup_save
     * @param params the get to save.
     * @returns returns an empty promise which resolves when the save has completed.
     */
    public async saveUserGroup(params: UserGroupGet): Promise<{ usergroup_keyno: `${number}` | number }> {
        return (await this.datatask.Post(100, params))[0];
    }

    /**
     * Deletes the given usergroup.
     *
     * @datatask
     * @datataskName tt_usergroup_delete
     * @param params the user group to delete.
     * @returns empty promise which resolves when the delete has completed.
     */
    public async deleteUserGroup(params: UserGroupGet): Promise<void> {
        try {
            const response = await this.datatask.Post(3830, params);

            if (!!response && typeof response === 'object' && 'validation' in response && !!response.validation) throw Error(response.message);

            this.setCurrentUserGroup(null);
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
        }
    }
}

export interface UserGroupGet {
    heading: string;
    usergroup_keyno: `${number}` | number;
    usergroup_name: string;
    dba_rights: '1' | '0';
    show_delete: '1' | '0';
    is_terminal_group: '1' | '0';
}

export interface GetUserGroupParams {
    usergroup_keyno: `${number}` | number;
}

export interface UserGroupSettingGet { setting_name: string; video_url: string; }
export interface UserGroupSettingEditGet {
    p2_usergroup_setting_keyno: string | number;
    p2_setting_keyno?: string | number;
    usergroup_name: string;
    setting_name: string;
    setting_value: string;
}
export interface UserGroupTableParams { argtype: string; argvalue: string; }
export interface UserGroupTableEditGet {
    p2_usergroup_table_keyno: string | number;
    table_name: string;
    usergroup_name: string;
    recordright_no: string;
}
